import { openNotificationWithIcon } from '../../utils/notification.helper'
import AssignmentsService from '../../services/assignments.service'
import InstantFeedbackService from '../../services/instant-feedback.service'
import { handleErrors } from '../../utils/request.helper'
import UsersService from '../../services/users.service'
import { EXCEL_ANALYSIS_TYPES } from '../../constants/excel-analysis-types'
import AssignmentService from '../../services/assignment.service'
import { ASSIGNMENT_PROCESSING_STATUSES } from '../../constants/assignment-processing-statuses'
import { actions as authorizedLayoutActions } from './authorized-layout.reducer'
import { ASSIGNMENT_TAG } from '../apis/utils/tags'
import { toggleCreateComputerScienceAssignmentModal } from '../slices/assignment.slice'
import baseApi from '../apis/base.api'

/***************************************** INITIAL STATE ********************************************/
export const INITIAL_STATE = {
    /** assignments list **/
    assignments: [],
    totalPages: 0,
    isWaitingForAssignments: false,
    assignmentsParams: {},
    /** upload modals **/
    excelSheetTypes: [],
    excelFiles: {
        answerKey: null,
        submissionsZip: null,
    },
    submissionsFileList: [],
    modelNames: [],
    isWaitingForModelNames: false,
    assignmentName: '',
    /** instant feedback modal **/
    starterCode: '',
    assignmentStudentsFeedback: [],
}

/***************************************** ACTION TYPES *********************************************/
const reducerName = 'ASSIGNMENTS_'
const actionTypes = {
    /** assignments list **/
    SET_ASSIGNMENTS: `${reducerName}SET_ASSIGNMENTS`,
    SET_TOTAL_PAGES: `${reducerName}SET_TOTAL_PAGES`,
    SET_IS_WAITING_FOR_ASSIGNMENTS: `${reducerName}SET_IS_WAITING_FOR_ASSIGNMENTS`,
    SET_ASSIGNMENTS_PARAMS: `${reducerName}SET_ASSIGNMENTS_PARAMS`,
    /** upload modals **/
    SET_EXCEL_SHEET_TYPES: `${reducerName}SET_EXCEL_SHEET_TYPES`,
    SET_EXCEL_ANSWER_KEY_FILE: `${reducerName}SET_EXCEL_ANSWER_KEY_FILE`,
    SET_EXCEL_SUBMISSIONS_ZIP_FILE: `${reducerName}SET_EXCEL_SUBMISSIONS_ZIP_FILE`,
    SET_SUBMISSIONS_FILE_LIST: `${reducerName}SET_SUBMISSIONS_FILE_LIST`,
    SET_MODEL_NAMES: `${reducerName}SET_MODEL_NAMES`,
    SET_IS_WAITING_FOR_MODEL_NAMES: `${reducerName}SET_IS_WAITING_FOR_FILE_TO_UPLOAD`,
    SET_ASSIGNMENT_NAME: `${reducerName}SET_ASSIGNMENT_NAME`,
    /** instant feedback modal **/
    SET_STARTER_CODE: `${reducerName}SET_STARTER_CODE`,
    SET_ASSIGNMENT_STUDENTS_FEEDBACK: `${reducerName}SET_ASSIGNMENT_STUDENTS_FEEDBACK`,
}

/******************************************** ACTIONS ***********************************************/
export const actions = {
    /** assignments list **/
    setAssignments: values => ({
        type: actionTypes.SET_ASSIGNMENTS,
        payload: { values },
    }),
    setTotalPages: values => ({
        type: actionTypes.SET_TOTAL_PAGES,
        payload: { values },
    }),
    fetchAssignments:
        (name, sort, type, language, page, pageSize, organizationIds, courseIds, useParamsFromQuery, searchType = 1) =>
        async (dispatch, getState) => {
            dispatch(actions.setIsWaitingForAssignments(true))
            await handleErrors(async () => {
                const assignmentsParams = getState().assignments.assignmentsParams
                let getAssignmentsResponse
                // we need this check not to pass all query params (name, sort, type, page, pageSize, useParamsFromQuery) in all places we do refetch of the assignments, instead we just store its copy in state and reuse it
                if (useParamsFromQuery) {
                    getAssignmentsResponse = await AssignmentsService.getAssignments(
                        name,
                        sort,
                        type,
                        language,
                        page,
                        pageSize,
                        organizationIds,
                        courseIds,
                        searchType
                    )
                    dispatch(
                        actions.setAssignmentsParams({
                            name,
                            sort,
                            type,
                            language,
                            page,
                            pageSize,
                            organizationIds,
                            courseIds,
                            searchType,
                        }),
                    )
                } else {
                    getAssignmentsResponse = await AssignmentsService.getAssignments(
                        assignmentsParams.name,
                        assignmentsParams.sort,
                        assignmentsParams.type,
                        assignmentsParams.language,
                        assignmentsParams.page,
                        assignmentsParams.pageSize,
                        assignmentsParams.organizationIds,
                        assignmentsParams.courseIds,
                        assignmentsParams.searchType
                    )
                }
                dispatch(actions.setAssignments(getAssignmentsResponse.data.assignments))
                dispatch(actions.setTotalPages(getAssignmentsResponse.data.total_pages))
            })
            dispatch(actions.setIsWaitingForAssignments(false))
        },
    updateAssignmentsProgress: listOfTokens => async (dispatch) => {
        return await handleErrors(async () => {
            const getAssignmentsProgressResponse = await AssignmentsService.getAssignmentsProgress(listOfTokens)
            const assignmentsToUpdate = getAssignmentsProgressResponse.data
            const hasSomeAssignmentFinishedProcessing = assignmentsToUpdate.some(
                assignment => assignment?.processing_status === ASSIGNMENT_PROCESSING_STATUSES.READY,
            )
            if (hasSomeAssignmentFinishedProcessing) {
                dispatch(baseApi.util.invalidateTags([{ type: ASSIGNMENT_TAG, id: 'LIST' }]))
            } else {
                assignmentsToUpdate.forEach(assignment => {
                    dispatch(baseApi.util.updateQueryData(
                        'fetchAssignments', 
                        undefined, 
                        draft => {
                            const assignmentToUpdate = draft?.assignments?.find(a => a.token === assignment.token)
                            if (assignmentToUpdate) {
                                assignmentToUpdate.current_version.processing_msg = assignment.processing_msg
                                assignmentToUpdate.current_version.processing_progress = assignment.processing_progress
                                assignmentToUpdate.current_version.processing_status = assignment.processing_status
                                assignmentToUpdate.current_version.total_files_no = assignment.total_files_no
                            }
                        }
                    ))
                })
            }
        })
    },
    deleteAssignment: token => async (dispatch) => {
        return await handleErrors(async () => {
            await AssignmentsService.deleteAssignment(token)
            dispatch(baseApi.util.invalidateTags([{ type: ASSIGNMENT_TAG, id: 'LIST' }]))
            openNotificationWithIcon('success', 'Deleted successfully')
        })
    },
    setIsWaitingForAssignments: value => ({
        type: actionTypes.SET_IS_WAITING_FOR_ASSIGNMENTS,
        payload: { value },
    }),
    setAssignmentsParams: value => ({
        type: actionTypes.SET_ASSIGNMENTS_PARAMS,
        payload: { value },
    }),
    /** upload modals **/
    setExcelSheetTypes: value => ({
        type: actionTypes.SET_EXCEL_SHEET_TYPES,
        payload: { value },
    }),
    setExcelAnswerKeyFile: value => ({
        type: actionTypes.SET_EXCEL_ANSWER_KEY_FILE,
        payload: { value },
    }),
    setExcelSubmissionsZipFile: value => ({
        type: actionTypes.SET_EXCEL_SUBMISSIONS_ZIP_FILE,
        payload: { value },
    }),
    setSubmissionsFileList: values => ({
        type: actionTypes.SET_SUBMISSIONS_FILE_LIST,
        payload: { values },
    }),
    setIsWaitingForModelNames: value => ({
        type: actionTypes.SET_IS_WAITING_FOR_MODEL_NAMES,
        payload: { value },
    }),
    setModelNames: value => ({
        type: actionTypes.SET_MODEL_NAMES,
        payload: { value },
    }),
    setAssignmentName: value => ({
        type: actionTypes.SET_ASSIGNMENT_NAME,
        payload: { value },
    }),
    uploadAssignment: (name, type, requireTrainset) => async (dispatch, getState) => {
        return await handleErrors(async () => {
            await AssignmentsService.uploadZip(
                getState().assignments.submissionsFileList[0],
                name,
                type,
                null,
                requireTrainset,
            )
            dispatch(baseApi.util.invalidateTags([{ type: ASSIGNMENT_TAG, id: 'LIST' }]))
            dispatch(actions.setSubmissionsFileList([]))
            dispatch(toggleCreateComputerScienceAssignmentModal())
        })
    },
    downloadSubmissions: (token, modelName, includeHistory) => async () => {
        return await handleErrors(async () => {
            await AssignmentService.downloadSubmissions(token, modelName, includeHistory)
        })
    },
    fetchModels: () => async dispatch => {
        dispatch(actions.setIsWaitingForModelNames(true))
        await handleErrors(async () => {
            const response = await AssignmentsService.getModels()
            dispatch(actions.setModelNames(response.data))
        })
        dispatch(actions.setIsWaitingForModelNames(false))
    },
    fetchPublishedModelVersions: token => async () => {
        return await handleErrors(async () => {
            const response = await AssignmentService.getAssignmentVersions(token, true)
            return response.data
        })
    },
    covertToModel: token => async (dispatch) => {
        return await handleErrors(async () => {
            await AssignmentsService.convertToModel(token)
            dispatch(baseApi.util.invalidateTags([{ type: ASSIGNMENT_TAG, id: token }]))
            openNotificationWithIcon('success', 'Converted successfully')
            return true
        })
    },
    migrateMBAVersion: (token, versionId) => async dispatch => {
        return await handleErrors(async () => {
            const { data: newVersion } = await AssignmentsService.migrateMBAVersion(token, versionId)
            dispatch(baseApi.util.invalidateTags([{ type: ASSIGNMENT_TAG, id: token }]))
            openNotificationWithIcon('success', 'Migrated successfully')
            return newVersion
        })
    },
    copyAssignment: (token, userId, assigmentName) => async dispatch => {
        return await handleErrors(async () => {
            await AssignmentsService.copyAssignment(token, userId, assigmentName)
            dispatch(baseApi.util.invalidateTags([{ type: ASSIGNMENT_TAG, id: token }]))
            openNotificationWithIcon('success', 'Copied successfully')
            return true
        })
    },
    fetchUsers: () => async () => {
        return await handleErrors(async () => {
            const getUsersResponse = await UsersService.getUsers()
            return getUsersResponse.data
        })
    },
    createPredefinedAssignment: (modelToken, modelVersionId) => async (dispatch, getState) => {
        return await handleErrors(async () => {
            await AssignmentsService.createPredefinedAssignment(
                modelToken,
                modelVersionId,
                getState().assignments.assignmentName,
            )
            openNotificationWithIcon('success', 'Assignment created successfully')
            dispatch(baseApi.util.invalidateTags([{ type: ASSIGNMENT_TAG, id: 'LIST' }]))
            dispatch(authorizedLayoutActions.setShowCreateAssignmentModal(false))
            dispatch(actions.setSubmissionsFileList([]))
        })
    },
    /** instant feedback modal **/
    setStarterCode: value => ({
        type: actionTypes.SET_STARTER_CODE,
        payload: { value },
    }),
    setAssignmentStudentsFeedback: value => ({
        type: actionTypes.SET_ASSIGNMENT_STUDENTS_FEEDBACK,
        payload: { value },
    }),
    updateAssignmentName: (token, assignmentName) => async (dispatch) => {
        return await handleErrors(async () => {
            await AssignmentsService.updateAssignmentName(token, assignmentName)
            dispatch(baseApi.util.invalidateTags([{ type: ASSIGNMENT_TAG, id: token }]))
            openNotificationWithIcon('success', 'Updated successfully')
        })
    },
    updateAssignmentProcessingStatus: (token, processingStatus) => async (dispatch) => {
        return await handleErrors(async () => {
            dispatch(baseApi.util.updateQueryData(
                'fetchAssignments', 
                undefined, 
                draft => {
                    const assignmentToUpdate = draft?.assignments?.find(a => a.token === token)
                    if (assignmentToUpdate) {
                        assignmentToUpdate.current_version.processing_status = processingStatus
                    }
                }
            ))
            dispatch(baseApi.util.invalidateTags([{ type: ASSIGNMENT_TAG, id: token }]))
        })
    },
    updateAssignmentTotalFilesStatus: (token, totalFilesNo) => async (dispatch) => {
        return await handleErrors(async () => {
            dispatch(baseApi.util.updateQueryData(
                'fetchAssignments', 
                undefined, 
                draft => {
                    const assignmentToUpdate = draft?.assignments?.find(a => a.token === token)
                    if (assignmentToUpdate) {
                        assignmentToUpdate.current_version.total_files_no = totalFilesNo
                    }
                }
            ))
            dispatch(baseApi.util.invalidateTags([{ type: ASSIGNMENT_TAG, id: token }]))
        })
    },
    saveStarterCode: token => async (dispatch, getState) => {
        return await handleErrors(async () => {
            await AssignmentsService.updateStarterCode(token, getState().assignments.starterCode)
            openNotificationWithIcon('success', 'Saved successfully')
        })
    },
    saveInstructions: (token, instructions) => async () => {
        return await handleErrors(async () => {
            await AssignmentsService.updateInstructions(token, instructions)
            openNotificationWithIcon('success', 'Saved successfully')
        })
    },
    fetchStarterCode: token => async dispatch => {
        return await handleErrors(async () => {
            const getStarterCodeResponse = await AssignmentsService.getStarterCode(token)
            dispatch(actions.setStarterCode(getStarterCodeResponse.data))
        })
    },
    fetchInstructions: token => async () => {
        return await handleErrors(async () => {
            const getInstructionsResponse = await AssignmentsService.getInstructions(token)
            return getInstructionsResponse.data
        })
    },
    fetchAssignmentStudentsFeedback: token => async dispatch => {
        return await handleErrors(async () => {
            const getAssignmentStudentsFeedbackResponse =
                await InstantFeedbackService.getAssignmentStudentsFeedback(token)
            dispatch(actions.setAssignmentStudentsFeedback(getAssignmentStudentsFeedbackResponse.data))
        })
    },
    getExcelSheets: file => async dispatch => {
        return await handleErrors(async () => {
            const getExcelSheetsResponse = await AssignmentsService.getExcelSheets(file)
            dispatch(
                actions.setExcelSheetTypes(
                    getExcelSheetsResponse.data.map(sheet => ({
                        name: sheet,
                        type: EXCEL_ANALYSIS_TYPES.BY_VALUE.value,
                    })),
                ),
            )
            dispatch(actions.setExcelAnswerKeyFile(file))
            return getExcelSheetsResponse.data
        })
    },
    uploadExcelAssignment: assignmentName => async (dispatch, getState) => {
        return await handleErrors(async () => {
            await AssignmentsService.uploadExcel(
                getState().assignments.excelFiles.answerKey,
                getState().assignments.excelFiles.submissionsZip,
                assignmentName,
                getState().assignments.excelSheetTypes,
            )
            dispatch(baseApi.util.invalidateTags([{ type: ASSIGNMENT_TAG, id: 'LIST' }]))
            dispatch(actions.setExcelSubmissionsZipFile(null))
            dispatch(authorizedLayoutActions.setShowCreateExcelAssignmentModal(false))
            openNotificationWithIcon('success', 'Created successfully')
        })
    },
}

/******************************************* REDUCER ************************************************/
const assignmentsReducer = (state = INITIAL_STATE, action) => {
    switch (action.type) {
        /** assignments list **/
        case actionTypes.SET_ASSIGNMENTS:
            return {
                ...state,
                assignments: action.payload.values,
            }

        case actionTypes.SET_TOTAL_PAGES:
            return {
                ...state,
                totalPages: action.payload.values,
            }

        case actionTypes.SET_IS_WAITING_FOR_ASSIGNMENTS:
            return {
                ...state,
                isWaitingForAssignments: action.payload.value,
            }

        case actionTypes.SET_ASSIGNMENTS_PARAMS:
            return {
                ...state,
                assignmentsParams: action.payload.value,
            }

        /** upload modals **/
        case actionTypes.SET_SHOW_CREATE_MODEL_MODAL:
            return {
                ...state,
                showCreateModelModal: action.payload.value,
            }

        case actionTypes.SET_SHOW_CREATE_ASSIGNMENT_MODAL:
            return {
                ...state,
                showCreateAssignmentModal: action.payload.value,
            }

        case actionTypes.SET_SHOW_CREATE_EXCEL_ASSIGNMENT_MODAL:
            return {
                ...state,
                showCreateExcelAssignmentModal: action.payload.value,
            }

        case actionTypes.SET_EXCEL_SHEET_TYPES:
            return {
                ...state,
                excelSheetTypes: action.payload.value,
            }

        case actionTypes.SET_EXCEL_ANSWER_KEY_FILE:
            return {
                ...state,
                excelFiles: {
                    ...state.excelFiles,
                    answerKey: action.payload.value,
                },
            }

        case actionTypes.SET_EXCEL_SUBMISSIONS_ZIP_FILE:
            return {
                ...state,
                excelFiles: {
                    ...state.excelFiles,
                    submissionsZip: action.payload.value,
                },
            }

        case actionTypes.SET_SUBMISSIONS_FILE_LIST:
            return {
                ...state,
                submissionsFileList: action.payload.values,
            }

        case actionTypes.SET_MODEL_NAMES:
            return {
                ...state,
                modelNames: action.payload.value,
            }

        case actionTypes.SET_IS_WAITING_FOR_MODEL_NAMES:
            return {
                ...state,
                isWaitingForModelNames: action.payload.value,
            }

        case actionTypes.SET_ASSIGNMENT_NAME:
            return {
                ...state,
                assignmentName: action.payload.value,
            }

        /** instant feedback modal **/
        case actionTypes.SET_STARTER_CODE:
            return {
                ...state,
                starterCode: action.payload.value,
            }

        case actionTypes.SET_ASSIGNMENT_STUDENTS_FEEDBACK:
            return {
                ...state,
                assignmentStudentsFeedback: action.payload.value,
            }

        default:
            return state
    }
}

export default assignmentsReducer
